/* eslint-disable arrow-parens */
import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { NoWorkspaceGuard } from 'app/core/auth/guards/no-workspace.guard';
import { PendingSetupGuard } from 'app/core/auth/guards/pending-setup.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { RoleGuard } from './core/auth/guards/role.guard';
import { TenantAuthGuard } from './core/auth/guards/tenant-auth.guard';
import { PersonaHomeGuard } from './core/auth/guards/persona-home.guard';
import { PersonaGuard } from './core/auth/guards/persona.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/sign-in'
    { path: '', pathMatch: 'full', redirectTo: 'sign-in' },

    // Deep link route for mobile app integration
    {
        path: 'join/meeting/:id',
        loadChildren: () =>
            import('app/modules/deep-link/deep-link.module').then(
                (m) => m.DeepLinkModule
            ),
    },

    // Redirect signed in user to the '/meeting'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    // SPEC-meet-ux-007 r2 AC-13 — straight to /sessions, never via the legacy URL.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'sessions' },

    // SESSION-meet-ux-017 (Phase 1) / GAP-MEETUX-IS-001: /home moved out of the
    // empty-layout smoke-test branch into the authenticated tenant-auth section
    // (below) so it inherits the futuristic layout (sidebar + topbar chrome) and
    // the proper guards. The smoke-test variant is gone; LandingHomeComponent is
    // now Avery's IS home.

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.module'
                    ).then((m) => m.AuthForgotPasswordModule),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.module'
                    ).then((m) => m.AuthResetPasswordModule),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule
                    ),
            },
            {
                path: 'activate',
                loadChildren: () =>
                    import('app/modules/auth/activate/activate.module').then(
                        (m) => m.AuthActivateModule
                    ),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'tenant-selection',
                loadChildren: () =>
                    import(
                        'app/modules/auth/tenant-selection/tenant-selection.module'
                    ).then((m) => m.TenantSelectionModule),
            },
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.module').then(
                        (m) => m.AuthSignOutModule
                    ),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.module'
                    ).then((m) => m.AuthUnlockSessionModule),
            },
            {
                path: 'role-routing',
                loadChildren: () =>
                    import(
                        'app/modules/auth/role-routing/role-routing.module'
                    ).then((m) => m.AuthRoleRoutingModule),
            },
            // SPEC-web-planner-013 Phase 1 (R33). A person who signs in with no
            // workspace lands here from sign-in.component.ts: clients are created
            // by Awvent, never from the browser, so there is no create flow. The
            // old /setup-wizard route (it created a workspace) is gone; a
            // PendingSetup client's Admin uses /setup (below). NoWorkspaceGuard
            // admits a zero-tenant user and sends anyone with tenants to their home.
            {
                path: 'workspace-pending',
                canActivate: [NoWorkspaceGuard],
                loadChildren: () =>
                    import(
                        'app/modules/auth/workspace-pending/workspace-pending.module'
                    ).then((m) => m.AuthWorkspacePendingModule),
            },
            // SPEC-web-planner-013 Phase 3 (AC-12, AC-13). The same holding page for
            // a member whose client is not ready (?state=pending). It has no
            // NoWorkspaceGuard: that guard sends anyone with a client through
            // redirectAfterLogin, which would send them straight back here.
            {
                path: 'workspace-status',
                loadChildren: () =>
                    import(
                        'app/modules/auth/workspace-pending/workspace-pending.module'
                    ).then((m) => m.AuthWorkspacePendingModule),
            },
            // "Set up your organisation" for the Admin of a PendingSetup client.
            {
                path: 'setup',
                canActivate: [PendingSetupGuard],
                loadChildren: () =>
                    import(
                        'app/modules/auth/organisation-setup/organisation-setup.module'
                    ).then((m) => m.AuthOrganisationSetupModule),
            },
        ],
    },

    // Admin routes
    // {
    //     path: '',
    //     canActivate: [AuthGuard],
    //     canActivateChild: [AuthGuard],
    //     component: LayoutComponent,
    //     resolve: {
    //         initialData: InitialDataResolver,
    //     },
    //     children: [
    //         { path: 'example', loadChildren: () => import('app/modules/admin/example/example.module').then(m => m.ExampleModule) },
    //     ]
    // },

    // User settings routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'user-settings',
                loadChildren: () =>
                    import('app/modules/settings/settings.module').then(
                        (m) => m.SettingsModule
                    ),
            },
            // SPEC-webplanner-025 AC-04 — /oauth/:provider/callback consumer.
            // Sits under AuthGuard so the rep is still signed in when Google
            // redirects back with ?code=&state=.
            {
                path: 'oauth',
                loadChildren: () =>
                    import('app/modules/oauth/oauth.module').then(
                        (m) => m.OAuthModule
                    ),
            },
        ],
    },

    // Tenant-Auth routes for application features
    {
        path: '',
        canActivate: [TenantAuthGuard],
        canActivateChild: [TenantAuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            // {
            //     path: 'admin',
            //     loadChildren: () => import('./../../projects/admin/src/lib/admin.module').then(m => m.AdminModule)
            // },
            // SPEC-web-planner-007 AC-17 (B10) — SuperAdmin only via PersonaGuard.
            {
                path: 'admin/subscription',
                canActivate: [PersonaGuard],
                data: { persona: 'superadmin' },
                loadChildren: () =>
                    import(
                        './modules/admin/subscription/subscription.module'
                    ).then((m) => m.SubscriptionModule),
            },
            {
                path: 'accounts',
                loadChildren: () =>
                    import(
                        './modules/business-accounts/business-accounts.module'
                    ).then((m) => m.BusinessAccountsModule),
            },
            {
                path: 'contacts',
                loadChildren: () =>
                    import('./modules/contacts/contacts.module').then(
                        (m) => m.ContactsModule
                    ),
            },
            // SPEC-meet-ux-007 r2 AC-13 — meetings = sessions (operator ruling
            // 2026-09-28). The legacy MeetingModule list duplicated /sessions and
            // rendered 0 rows, so every /meetings URL now redirects to its /sessions
            // equivalent and no legacy modules/meeting component is routed.
            { path: 'meetings', pathMatch: 'full', redirectTo: '/sessions' },
            { path: 'meetings/details/:id', redirectTo: '/sessions/:id' },
            { path: 'meetings/edit/:id', redirectTo: '/sessions/:id' },
            { path: 'meetings/new/:id', redirectTo: '/sessions/new' },
            // SESSION-meet-ux-017 (Phase 1) / GAP-MEETUX-IS-001: IS home, mounted
            // under the futuristic layout so it auto-inherits sidebar + topbar
            // chrome. RoleGuard consumed per SPEC AC-11; gating against
            // FS/CRO/Admin happens at the sidebar-visibility layer (RoleGuard
            // as implemented only reroutes SuperAdmin — see
            // decisions/2026-06-11-admin-settings-roleguard.md).
            {
                path: 'home',
                canActivate: [RoleGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/landing/home/home.module').then(
                        (m) => m.LandingHomeModule
                    ),
            },
            // SESSION-meet-ux-017 (Phase 1) — NEW /sessions module replaces the
            // phantom sidebar entry. /meetings stays live as a legacy fallback;
            // a later cleanup session retires it (Q1 default 2026-06-18).
            {
                path: 'sessions',
                canActivate: [RoleGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/sessions/sessions.module').then(
                        (m) => m.SessionsModule
                    ),
            },
            // SESSION-webplanner-022 / OQ-WP-022-01 — orphan /is/sessions
            // surface deleted; canonical lives at /sessions. The InsideSalesModule
            // shell is retained for future IS-only surfaces but its lazy `/is`
            // mount is gone; these redirects keep external bookmarks alive.
            { path: 'is/sessions', pathMatch: 'full', redirectTo: '/sessions' },
            { path: 'is/sessions/new', pathMatch: 'full', redirectTo: '/sessions/new' },
            // SPEC-meet-ux-021 / GAP-MEET-UX-009 — the design master retires
            // the standalone `/opportunities` route in favour of the
            // `/accounts?view=opps` view-toggle contract. Bare `/opportunities`
            // redirects to the folded-in All-opportunities view. The lazy-load
            // stays live for `/opportunities/new` (the New-opportunity wizard —
            // SPEC-meet-ux-017 Phase 3 / GAP-MEETUX-IS-001, untouched by this
            // SPEC) via the second entry below; `pathMatch: 'full'` on the
            // redirect only catches the bare URL.
            { path: 'opportunities', pathMatch: 'full', redirectTo: '/accounts?view=opps' },
            {
                path: 'opportunities',
                canActivate: [RoleGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/opportunities/opportunities.module').then(
                        (m) => m.OpportunitiesModule
                    ),
            },
            // SPEC-meet-ux-019 / GAP-MEET-UX-003 — opportunity-scoped
            // Qualification review page. Declared BEFORE the '/queue' list
            // entry below so the more specific two-segment URL wins.
            {
                path: 'queue/:oppId',
                // SPEC-web-planner-007 AC-17 (OQ-2) — Inside Sales only.
                data: { persona: 'is' },
                canActivate: [RoleGuard, PersonaGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/queue/qualification-review/qualification-review.module').then(
                        (m) => m.QualificationReviewModule
                    ),
            },
            // IS-persona Phase 0.2 (2026-08-17) — the /queue LIST. Was served
            // by the catch-all landing stubs module at the bottom of this table
            // (as LandingQueueComponent); it is a shipped rep-facing page, not
            // a stub, so it now owns a lazy module like every other real page.
            // Route string and rendered output are unchanged.
            // NOTE: deliberately carries NO per-route guard, because the
            // stubs entry it moved out of carried none either — both pages ran
            // under the parent's TenantAuthGuard (canActivateChild) alone.
            // Adding RoleGuard/AuthGuard here to match the sibling pages would
            // be a behaviour change, which this move is not allowed to make.
            // ⚠ GAP-MEET-UX-031 (filed 2026-08-18, Prashant RULING 4) owns this
            // question and is NOT yet decided: it needs a security-posture call
            // on whether role gating should apply, because adding the guards
            // could lock out a role that reads these pages today. Do NOT add
            // guards here without that ruling. Note /queue/:oppId above IS
            // guarded while /queue is not — that asymmetry is the GAP's
            // sharpest evidence. See IS-PERSONA-PROGRESS.md Note C for origin.
            // SPEC-web-planner-007 AC-17 (OQ-2, operator 2026-10-06) — Inside
            // Sales only via PersonaGuard; this supersedes the no-guard note above.
            {
                path: 'queue',
                canActivate: [PersonaGuard],
                data: { persona: 'is' },
                loadChildren: () =>
                    import('./modules/queue/queue-list/queue-list.module').then(
                        (m) => m.QueueListModule
                    ),
            },
            // IS-persona Phase 0.2 (2026-08-17) — /activity, the audit-trail
            // leg of the IS journey. Same story: shipped page (GAP-WEBPLANNER-032
            // reskin + GAP-BACKEND-IS-010 live projection), previously declared
            // by the landing stubs module as LandingActivityStubComponent.
            // Same no-guard note as /queue above — GAP-MEET-UX-031, undecided.
            // /activity is an audit trail, so it may warrant a stricter posture
            // than /queue; the GAP lists that as decision option (c).
            // SPEC-web-planner-007 AC-17 (OQ-2) — Inside Sales only via PersonaGuard.
            {
                path: 'activity',
                canActivate: [PersonaGuard],
                data: { persona: 'is' },
                loadChildren: () =>
                    import('./modules/activity/activity.module').then(
                        (m) => m.ActivityModule
                    ),
            },
            // SESSION-meet-ux-017 (Phase 4) — IS post-activation onboarding tour.
            // Linked from `/activate` success state (Q5 default 2026-06-18).
            {
                path: 'getting-started',
                canActivate: [RoleGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/getting-started/getting-started.module').then(
                        (m) => m.GettingStartedModule
                    ),
            },
            {
                // SPEC-meet-ux-007 follow-up (2026-06-18). Baseline-flow Step 6
                // minimum: standalone /deals/add page for IS to create an
                // Opportunity tied to a BusinessAccount (no CRM sync). Full deals
                // list / detail pages land with GAP-MEETUX-019 (FS deals home).
                path: 'deals',
                canActivate: [RoleGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/deals/deals.module').then(
                        (m) => m.DealsModule
                    ),
            },
            {
                path: 'products',
                canActivate: [RoleGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/products/products.module').then(
                        (m) => m.ProductsModule
                    ),
            },
            // SESSION-admin-ptg-004 / GAP-ADMIN-PTG-004 — Admin Teams & Groups
            // page. Sidebar entry switched to /teams (was the phantom
            // /admin/teams). RoleGuard reroutes SuperAdmin only; non-admin
            // visibility is gated at the sidebar level.
            {
                path: 'teams',
                // SPEC-web-planner-007 AC-17 (B10) — admin only.
                data: { persona: 'admin' },
                canActivate: [RoleGuard, PersonaGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/teams/teams.module').then(
                        (m) => m.TeamsModule
                    ),
            },
            // SPEC-web-planner-024 AC-1 (supersedes SPEC-web-planner-007 AC-19): /admin/integrations was an empty
            // placeholder and /admin/data was never registered. Both go to /admin (itself admin-only); no control links here.
            { path: 'admin/integrations', pathMatch: 'full', redirectTo: '/admin' },
            { path: 'admin/data', pathMatch: 'full', redirectTo: '/admin' },
            // SPEC-web-planner-026 AC-9 (GAP-MEET-UX-323): the Settings page was only "Coming soon"; its stub and nav entry are gone.
            // SPEC-web-planner-035 AC-1: /admin/settings is now the Admin-only Workspace settings page (same guards as /teams).
            {
                path: 'admin/settings',
                data: { persona: 'admin' },
                canActivate: [RoleGuard, PersonaGuard],
                canActivateChild: [AuthGuard],
                loadChildren: () =>
                    import('./modules/workspace-settings/workspace-settings.module').then(
                        (m) => m.WorkspaceSettingsModule
                    ),
            },
            // SPEC-web-planner-007 AC-17 (C11) — PersonaGuard as canActivateChild
            // on the stubs mount: URLs matching ^/admin require the admin persona,
            // with no edit to stubs.routing.ts.
            {
                path: '',
                canActivateChild: [PersonaGuard],
                loadChildren: () =>
                    import('./modules/landing/stubs/stubs.module').then(
                        (m) => m.LandingStubsModule
                    ),
            },
        ],
    },

    // GAP-MEET-UX-213 — trailing wildcard. Any URL none of the above tables
    // registered (a mistyped path, or a future phantom nav item) redirects to
    // the current persona's home instead of rendering a blank shell. Must
    // stay LAST: the lazy stubs `path: ''` child above needs its chance
    // first, and the router backs out of it to this entry.
    { path: '**', canActivate: [PersonaHomeGuard], children: [] },
];
